// src/Views/EnterPasswordView.js
import React, { useState, useRef, useEffect } from 'react';
import AuthService from '../services/AuthService';
import { BORDER_RADIUS, SPACING_COMPACT, SPACING_CARD, SPACING_CARD_BODY, SPACING_GROUP, SPACING_SECTION, SPACING_SECTION_LARGE, SPACING_HERO, SPACING_PAGE } from '../constants/layout';
import { FONT_TITLE2, FONT_BODY, FONT_SUBHEADLINE } from '../constants/typography';

function EnterPasswordView({ email, onBack, onClose }) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const passwordInputRef = useRef(null);

  useEffect(() => {
    passwordInputRef.current?.focus();
  }, []);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);

    try {
      await AuthService.signIn(email, password);
      onClose();
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        setErrorMessage('Incorrect password. Please try again.');
      } else if (error.code === 'auth/user-not-found') {
        setErrorMessage('No account found with this email.');
      } else {
        setErrorMessage(error.message || 'Failed to sign in. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    try {
      await AuthService.sendPasswordReset(email);
      alert(`Password reset email sent to ${email}`);
    } catch (error) {
      alert('Failed to send password reset email. Please try again.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Back button */}
        <button className="back-button" onClick={onBack} aria-label="Back">
          <span className="material-icons">arrow_back</span>
        </button>

        {/* Close button */}
        <button className="close-button" onClick={onClose} aria-label="Close">
          <span className="material-icons">close</span>
        </button>

        <div className="modal-content">
          <h2 className="modal-title">Enter your password</h2>
          <p className="modal-subtitle">{email}</p>

          <form onSubmit={handleSignIn}>
            <input
              ref={passwordInputRef}
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="password-input"
              autoComplete="current-password"
            />

            {errorMessage && <p className="error-message">{errorMessage}</p>}

            <button
              type="submit"
              disabled={!password || isLoading}
              className="submit-button"
            >
              {isLoading ? <span className="button-loader"></span> : 'Sign In'}
            </button>

            <button
              type="button"
              onClick={handleForgotPassword}
              className="forgot-password-button"
            >
              Forgot password?
            </button>
          </form>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: ${SPACING_CARD};
        }

        .modal-container {
          background-color: var(--color-card-background);
          border-radius: ${BORDER_RADIUS};
          max-width: 500px;
          width: 100%;
          position: relative;
        }

        .back-button, .close-button {
          position: absolute;
          top: ${SPACING_CARD};
          background: none;
          border: none;
          cursor: pointer;
          color: var(--color-text-secondary);
          padding: ${SPACING_COMPACT};
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.2s;
        }

        .back-button {
          left: ${SPACING_CARD};
        }

        .close-button {
          right: ${SPACING_CARD};
        }

        .back-button:hover, .close-button:hover {
          background-color: var(--color-surface-muted);
        }

        .modal-content {
          padding: ${SPACING_PAGE} ${SPACING_SECTION_LARGE} ${SPACING_HERO};
          display: flex;
          flex-direction: column;
          gap: ${SPACING_CARD_BODY};
        }

        .modal-title {
          font-size: ${FONT_TITLE2};
          font-weight: 600;
          text-align: center;
          margin: 0;
          color: var(--color-text-primary);
        }

        .modal-subtitle {
          font-size: ${FONT_BODY};
          text-align: center;
          color: var(--color-text-secondary);
          margin: 0;
        }

        form {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_CARD};
        }

        .password-input {
          width: 100%;
          padding: ${SPACING_CARD};
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          font-size: 1rem;
          background-color: var(--color-surface-muted);
          color: var(--color-text-primary);
          box-sizing: border-box;
        }

        .password-input:focus {
          outline: none;
          border-color: var(--color-accent);
        }

        .error-message {
          color: #ef4444;
          font-size: ${FONT_SUBHEADLINE};
          margin: 0;
        }

        .submit-button {
          width: 100%;
          height: 54px;
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          border: none;
          border-radius: ${BORDER_RADIUS};
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .submit-button:hover:not(:disabled) {
          background-color: var(--color-accent-hover);
          transform: translateY(-1px);
        }

        .submit-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .button-loader {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .forgot-password-button {
          background: none;
          border: none;
          color: var(--color-accent);
          font-size: ${FONT_SUBHEADLINE};
          cursor: pointer;
          text-align: center;
          padding: ${SPACING_COMPACT};
        }

        .forgot-password-button:hover {
          text-decoration: underline;
        }

        @media (max-width: 600px) {
          .modal-content {
            padding: ${SPACING_PAGE} ${SPACING_GROUP} ${SPACING_SECTION};
          }
        }
      `}</style>
    </div>
  );
}

export default EnterPasswordView;
